import type {
  AvailableTourTimes,
  InstantTourProviders,
  RequestTourBody,
  RescheduleTourBody,
  ScheduledTour,
  TourMutationResponse,
  TourProvider,
} from '@/src/lib/api/endpoints/hpTours';

export type RequestTourMutationBody = RequestTourBody & {
  zoneId?: string;
};

export type RescheduleTourMutationBody = RescheduleTourBody & {
  zoneId?: string;
};

const MOCK_TOUR_DAYS_TO_GENERATE = 14;
const DATE_KEY_PAD_LENGTH = 2;
const MOCK_TOUR_ID_PREFIX = 'mock-tour';
const MOCK_TOUR_STATUS = 'SCHEDULED';
const MOCK_TOUR_PROVIDERS = {
  inPersonProvider: {
    dateOnly: false,
    providerName: 'mock-in-person',
    supportsCancel: true,
  },
  selfGuidedProvider: {
    dateOnly: true,
    providerName: 'mock-self-guided',
    supportsCancel: true,
  },
  liveVideoProvider: {
    dateOnly: false,
    providerName: 'mock-live-video',
    supportsCancel: true,
  },
} satisfies Required<InstantTourProviders>;
const MOCK_TIMED_TOUR_SLOTS = [
  { hours: 9, minutes: 30 },
  { hours: 11, minutes: 0 },
  { hours: 13, minutes: 30 },
  { hours: 15, minutes: 0 },
] as const;

function getLocalTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles';
}

function formatLocalDateKey(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(DATE_KEY_PAD_LENGTH, '0');
  const day = String(date.getDate()).padStart(DATE_KEY_PAD_LENGTH, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

function createDateAtSlot(baseDate: Date, slot: (typeof MOCK_TIMED_TOUR_SLOTS)[number]): Date {
  const date = new Date(baseDate);
  date.setHours(slot.hours, slot.minutes, 0, 0);
  return date;
}

function createMockTourId({
  listingAlias,
  rentalSubmitId,
  requestedTime,
  tourType,
}: Pick<RequestTourMutationBody, 'listingAlias' | 'rentalSubmitId' | 'requestedTime' | 'tourType'>): string {
  return [
    MOCK_TOUR_ID_PREFIX,
    listingAlias,
    tourType.toLowerCase(),
    rentalSubmitId || requestedTime,
  ]
    .join('-')
    .replace(/[^a-z0-9-]+/gi, '-')
    .toLowerCase();
}

function mergeMockProvider(
  mockProvider: TourProvider,
  provider?: TourProvider | null,
): TourProvider {
  return { ...mockProvider, ...provider };
}

export function createMockInstantTourProviders(
  providers?: InstantTourProviders | null,
): InstantTourProviders {
  return {
    inPersonProvider: mergeMockProvider(
      MOCK_TOUR_PROVIDERS.inPersonProvider,
      providers?.inPersonProvider,
    ),
    selfGuidedProvider: mergeMockProvider(
      MOCK_TOUR_PROVIDERS.selfGuidedProvider,
      providers?.selfGuidedProvider,
    ),
    liveVideoProvider: mergeMockProvider(
      MOCK_TOUR_PROVIDERS.liveVideoProvider,
      providers?.liveVideoProvider,
    ),
  };
}

export function createMockAvailableTourTimes({
  dateOnly,
  now = new Date(),
  zoneId = getLocalTimeZone(),
}: {
  dateOnly?: boolean;
  now?: Date;
  zoneId?: string;
}): AvailableTourTimes {
  const availableTimes: Array<string> = [];
  const firstMockDate = new Date(now);
  firstMockDate.setHours(0, 0, 0, 0);

  for (let offset = 1; offset <= MOCK_TOUR_DAYS_TO_GENERATE; offset += 1) {
    const date = new Date(firstMockDate);
    date.setDate(firstMockDate.getDate() + offset);

    if (dateOnly) {
      availableTimes.push(formatLocalDateKey(date));
    } else {
      MOCK_TIMED_TOUR_SLOTS.forEach((slot) => {
        availableTimes.push(createDateAtSlot(date, slot).toISOString());
      });
    }
  }

  return {
    availableTimes,
    dateOnly: Boolean(dateOnly),
    zoneId,
  };
}

export function createMockRequestTourResponse(
  body: RequestTourMutationBody,
): TourMutationResponse {
  return {
    success: true,
    data: {
      listingAlias: body.listingAlias,
      tourId: body.tourId || createMockTourId(body),
      tourType: body.tourType,
      startTime: body.requestedTime,
      schedulingStatus: MOCK_TOUR_STATUS,
      supportsCancel: 'true',
      zoneId: body.zoneId,
    } satisfies ScheduledTour,
  };
}

export function createMockRescheduleTourResponse(
  body: RescheduleTourMutationBody,
): TourMutationResponse {
  return {
    success: true,
    data: {
      listingAlias: body.listingAlias,
      tourId: body.tourId,
      tourType: body.tourType,
      startTime: body.requestedTime,
      schedulingStatus: MOCK_TOUR_STATUS,
      supportsCancel: 'true',
      zoneId: body.zoneId,
    } satisfies ScheduledTour,
  };
}

export function createMockCancelTourResponse() {
  return { success: true };
}
